import Image from 'next/image'
import { markFillStyle, normalizeMarkBounds } from '@sifly/shared-utils'

/**
 * A dealer mark on a white plate, sized to the MARK rather than the file.
 *
 * Shared by the `brand_showcase` header badge (shield) and footer logo
 * (round) so the two can't drift — the second surface asking for this
 * treatment is what pulled it out of the header.
 *
 * The mark is positioned by `markFillStyle` from the asset's
 * `content_bounds` (migration 337), so the plate's size means the same
 * apparent mark size for every dealer, regardless of whether their file is a
 * tight crop, has transparent padding, or carries a baked-in backing plate.
 * Assets with no computed bounds fall back to whole-file framing — exactly
 * the pre-bounds behavior.
 */

/**
 * Clear space between the mark and the plate edge, as a PERCENTAGE of the
 * plate rather than a fixed px: it stays proportional across every plate
 * size (a fixed 5px reads heavy on a small plate and thin on a large one).
 * 6% is ~4.5px on the header's `md` badge.
 *
 * It must be an inset WRAPPER, not padding on the plate: the mark is
 * absolutely positioned, and an abs-positioned child resolves against its
 * containing block's PADDING box — so padding on the plate would be ignored
 * entirely and the mark would still bleed to the edge.
 */
// Not exported: only MarkPlate applies it, and knip's unused-export gate
// would flag it. Both surfaces get it by composing this component.
const MARK_CLEAR_SPACE = 'inset-[6%]'

export type MarkPlateShape =
  /** Square top, semicircular bottom — the header's badge-drop silhouette. */
  | 'shield'
  /** Full circle. */
  | 'round'

const SHAPE_CLASS: Record<MarkPlateShape, string> = {
  shield: 'rounded-b-full',
  round: 'rounded-full',
}

export function MarkPlate({
  src,
  alt,
  bounds,
  shape,
  sizeClass,
  className,
}: {
  src: string
  alt: string
  /** Raw `media_items.content_bounds` jsonb; invalid/absent → whole-file. */
  bounds?: unknown
  shape: MarkPlateShape
  /** Tailwind width/height classes for the plate (literal strings — the
   *  scanner can't see interpolated names). */
  sizeClass: string
  className?: string
}) {
  return (
    <span
      className={`relative block overflow-hidden ${sizeClass} ${SHAPE_CLASS[shape]} bg-white shadow-md ${className ?? ''}`}
    >
      <span className={`absolute ${MARK_CLEAR_SPACE} overflow-hidden`}>
        <Image
          src={src}
          alt={alt}
          width={160}
          height={160}
          style={markFillStyle(normalizeMarkBounds(bounds))}
          className="object-contain"
        />
      </span>
    </span>
  )
}
